"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DateUtils = void 0;
/*
 * Copyright © 2019 - Present, Vamstar Limited
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or
 * without modification, are not permitted.
 */
const format_1 = __importDefault(require("date-fns/format"));
const isValid_1 = __importDefault(require("date-fns/isValid"));
const parse_1 = __importDefault(require("date-fns/parse"));
const addDays_1 = __importDefault(require("date-fns/addDays"));
const addWeeks_1 = __importDefault(require("date-fns/addWeeks"));
const addMonths_1 = __importDefault(require("date-fns/addMonths"));
const isSameDay_1 = __importDefault(require("date-fns/isSameDay"));
const isSameWeek_1 = __importDefault(require("date-fns/isSameWeek"));
const isSameMonth_1 = __importDefault(require("date-fns/isSameMonth"));
const subDays_1 = __importDefault(require("date-fns/subDays"));
const subWeeks_1 = __importDefault(require("date-fns/subWeeks"));
const subMonths_1 = __importDefault(require("date-fns/subMonths"));
const startOfDay_1 = __importDefault(require("date-fns/startOfDay"));
const startOfWeek_1 = __importDefault(require("date-fns/startOfWeek"));
const startOfMonth_1 = __importDefault(require("date-fns/startOfMonth"));
const endOfDay_1 = __importDefault(require("date-fns/endOfDay"));
const endOfWeek_1 = __importDefault(require("date-fns/endOfWeek"));
const endOfMonth_1 = __importDefault(require("date-fns/endOfMonth"));
const duration_type_enum_1 = require("../common/enums/duration-type-enum");
const date_1 = require("../constants/date");
/**
 * @class DateUtils
 */
class DateUtils {
    /**
     * @method toISOString
     * @param date
     * @returns date after converting it into ISO string
     */
    static toISOString(date) {
        let isoDate;
        if (date && (0, isValid_1.default)(new Date(date))) {
            isoDate = new Date(date).toISOString();
        }
        return isoDate;
    }
    /**
     * @method parse
     * @param date
     * @returns parsed date in `yyyyMMdd` format
     */
    static parse(date) {
        let dateObj = new Date(date);
        if (!(0, isValid_1.default)(dateObj)) {
            dateObj = (0, parse_1.default)(date, date_1.YYYYMMDD_DATE_FORMAT, new Date());
        }
        return dateObj;
    }
    /**
     * @method parseDefault
     * @param date
     * @returns parsed date in `dd/MM/yyyy` format
     */
    static parseDefault(date, defaultDate = new Date()) {
        let dateObj = new Date(date);
        if (!(0, isValid_1.default)(dateObj)) {
            dateObj = (0, parse_1.default)(date, date_1.DEFAULT_DATE_FORMAT, defaultDate);
        }
        return dateObj;
    }
    /**
     * Parses the ISO or the number timestamps to date
     * @param dateStr
     * @returns
     */
    static parseTimestamp(dateStr) {
        if (dateStr) {
            let parsedTimestamp = Number(dateStr);
            if (Number.isNaN(parsedTimestamp)) {
                parsedTimestamp = new Date(dateStr);
            }
            return (0, isValid_1.default)(parsedTimestamp) ? new Date(parsedTimestamp) : null;
        }
        return null;
    }
    /**
     * @method reformat
     * @param oldDateStr
     * @returns Date in `dd/MM/yyyy` format
     */
    static reformat(oldDateStr) {
        let dateStr = '';
        if (oldDateStr) {
            dateStr = (0, format_1.default)(DateUtils.parse(oldDateStr), date_1.DEFAULT_DATE_FORMAT);
        }
        return dateStr;
    }
    /**
     * @method addDuration
     * @param date
     * @param type type of duration
     * @param amount amount of duration to be added to the date
     * @returns Date after adding the duration according to duration type
     */
    static addDuration(date, type, amount) {
        let newDate = new Date();
        if (type === duration_type_enum_1.DurationType.DAY) {
            newDate = (0, addDays_1.default)(date, amount);
        }
        if (type === duration_type_enum_1.DurationType.WEEK) {
            newDate = (0, addWeeks_1.default)(date, amount);
        }
        if (type === duration_type_enum_1.DurationType.MONTH) {
            newDate = (0, addMonths_1.default)(date, amount);
        }
        return newDate;
    }
    /**
     * @method subDuration
     * @param date
     * @param type type of duration
     * @param amount amount of duration to be subtracted to the date
     * @returns Date after subtracting the duration according to duration type
     */
    static subDuration(date, type, amount) {
        let newDate = new Date();
        if (type === duration_type_enum_1.DurationType.DAY) {
            newDate = (0, subDays_1.default)(date, amount);
        }
        if (type === duration_type_enum_1.DurationType.WEEK) {
            newDate = (0, subWeeks_1.default)(date, amount);
        }
        if (type === duration_type_enum_1.DurationType.MONTH) {
            newDate = (0, subMonths_1.default)(date, amount);
        }
        return newDate;
    }
    /**
     * @method isSameDuration
     * @param sourceDate
     * @param targetDate
     * @param type type of duration
     * @returns boolean value if the day or week or month of source and target dates are the same
     */
    static isSameDuration(sourceDate, targetDate, type) {
        let isSame = false;
        if (type === duration_type_enum_1.DurationType.DAY) {
            isSame = (0, isSameDay_1.default)(sourceDate, targetDate);
        }
        if (type === duration_type_enum_1.DurationType.WEEK) {
            isSame = (0, isSameWeek_1.default)(sourceDate, targetDate);
        }
        if (type === duration_type_enum_1.DurationType.MONTH) {
            isSame = (0, isSameMonth_1.default)(sourceDate, targetDate);
        }
        return isSame;
    }
    /**
     * @method startOfDuration
     * @param date
     * @param type type of duration
     * @returns start date of the week or month according to the duration type and date
     */
    static startOfDuration(date, type) {
        let startOfDate = (0, startOfDay_1.default)(date);
        if (type === duration_type_enum_1.DurationType.WEEK) {
            startOfDate = (0, startOfWeek_1.default)(date);
        }
        if (type === duration_type_enum_1.DurationType.MONTH) {
            startOfDate = (0, startOfMonth_1.default)(date);
        }
        return startOfDate;
    }
    /**
     * @method endOfDuration
     * @param date
     * @param type type of duration
     * @returns end date of the week or month according to the duration type and date
     */
    static endOfDuration(date, type) {
        let startOfDate = (0, endOfDay_1.default)(date);
        if (type === duration_type_enum_1.DurationType.WEEK) {
            startOfDate = (0, endOfWeek_1.default)(date);
        }
        if (type === duration_type_enum_1.DurationType.MONTH) {
            startOfDate = (0, endOfMonth_1.default)(date);
        }
        return startOfDate;
    }
}
exports.DateUtils = DateUtils;
/**
 * gets close date
 * @param searchResultUnion - Union of flat lot and entity
 * @returns - Date if present or returns null
 */
DateUtils.getCloseDate = (searchResultUnion) => {
    const { closeSubmissionDate, estimatedCloseSubmissionDate } = searchResultUnion;
    const closeDate = closeSubmissionDate || estimatedCloseSubmissionDate;
    const d = DateUtils.parseTimestamp(closeDate);
    return d;
};
/**
 * gets open date
 * @param searchResultUnion - Union of flat lot and entity
 * @returns - Date if present or returns null
 */
DateUtils.getOpenDate = (searchResultUnion) => {
    const { publishedDate, openSubmissionDate } = searchResultUnion;
    const openDate = publishedDate || openSubmissionDate;
    return DateUtils.parseTimestamp(openDate);
};
/**
 * gets the renewal date
 * @param searchResultUnion - Union of flat lot and entity
 * @returns - Date if present or returns undefined
 */
DateUtils.getRenewalDate = (searchResultUnion) => {
    const { estimatedOpenSubmissionDate } = searchResultUnion;
    return DateUtils.parseTimestamp(estimatedOpenSubmissionDate);
};
//# sourceMappingURL=data:application/json;base64,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